import type { ColumnDef } from '@workspace/silk-ui';

const CONTROL_COLUMN_IDS = new Set(['select', 'actions']);

export interface CommerceRegistryColumnOptions {
  mode: 'client' | 'manual';
  sortFields?: readonly string[];
  registry: string;
}

function resolveColumnId<TData, TValue>(column: ColumnDef<TData, TValue>): string | null {
  const candidate = column as ColumnDef<TData, TValue> & {
    accessorKey?: string | number;
    id?: string;
  };
  if (candidate.id?.trim()) return candidate.id.trim();
  return typeof candidate.accessorKey === 'string' && candidate.accessorKey.trim()
    ? candidate.accessorKey.trim()
    : null;
}

function hasSemanticAccessor<TData, TValue>(column: ColumnDef<TData, TValue>): boolean {
  const candidate = column as ColumnDef<TData, TValue> & {
    accessorFn?: unknown;
    accessorKey?: unknown;
    sortingFn?: unknown;
  };
  return (
    typeof candidate.accessorFn === 'function'
    || typeof candidate.accessorKey === 'string'
    || typeof candidate.sortingFn === 'function'
  );
}

/**
 * Declares and validates the column contract shared by operator-facing Commerce
 * registries. The returned array is unchanged so callers retain normal TanStack
 * Table typing while configuration mistakes fail close to their source.
 */
export function defineCommerceRegistryColumns<TData, TValue = unknown>(
  columns: ColumnDef<TData, TValue>[],
  options: CommerceRegistryColumnOptions,
): ColumnDef<TData, TValue>[] {
  const allowedSortFields = new Set(options.sortFields ?? []);

  for (const column of columns) {
    const id = resolveColumnId(column);
    if (!id) {
      throw new Error(`[${options.registry}] Commerce registry columns require a stable id or accessorKey.`);
    }

    if (CONTROL_COLUMN_IDS.has(id)) {
      if (column.enableSorting !== false) {
        throw new Error(`[${options.registry}] Control column "${id}" must explicitly disable sorting.`);
      }
      continue;
    }

    if (column.enableSorting === false) {
      throw new Error(`[${options.registry}] Semantic column "${id}" must support sorting.`);
    }
    if (!hasSemanticAccessor(column)) {
      throw new Error(
        `[${options.registry}] Semantic column "${id}" requires an accessor or sorting function for truthful sorting.`,
      );
    }
    if (options.mode === 'manual' && !allowedSortFields.has(id)) {
      throw new Error(
        `[${options.registry}] Semantic column "${id}" is missing from the manual server-sort allowlist.`,
      );
    }
  }

  return columns;
}

